import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useListStockReturnsQuery } from "../../stockApi";
import { useCanCreateReturn } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const RETURN_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "STOCK_OUT_BASED", label: "Stock-Out Based" },
  { value: "DIRECT_SUPPLIER", label: "Direct to Supplier" },
];

const StockReturnList = () => {
  const canCreate = useCanCreateReturn();
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [returnType, setReturnType] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListStockReturnsQuery({
    branchId: selectedBranchId ?? undefined,
    returnType: returnType || undefined,
    search: search || undefined,
    includeDeleted,
    pageSize: 100,
  });

  const columns = [
    {
      title: "Return No",
      dataIndex: "returnNo",
      render: (value, record) => <Link to={paths.stockReturnDetail(record.recordGuid)}>{value}</Link>,
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Type", dataIndex: "returnType", render: (v) => (v === "STOCK_OUT_BASED" ? "Stock-Out Based" : "Direct to Supplier") },
    { title: "Reference", dataIndex: "stockOutNo", render: (v, r) => v || r.supplierName || "-" },
    { title: "Date", dataIndex: "returnDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Total Amount", dataIndex: "totalAmount", render: (v) => Number(v || 0).toFixed(2) },
    {
      title: "Status",
      dataIndex: "isDeleted",
      render: (v) => (v ? <span className="badge badge-linedanger">Deleted</span> : <span className="badge badge-linesuccess">Posted</span>),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button type="button" className="btn btn-link p-2" title="View" onClick={() => navigate(paths.stockReturnDetail(record.recordGuid))}>
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock Returns</h4>
              <h6>Returned to stock from an issuance, or returned direct to a supplier</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockReturnNew)}>
                <PlusCircle className="me-2" size={16} />
                New Return
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search Return No"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-control" value={returnType} onChange={(e) => setReturnType(e.target.value)}>
                  {RETURN_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="return-include-deleted"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="return-include-deleted">Show deleted</label>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={data?.items || []} rowKey="recordGuid" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReturnList;
